<template>
    <b-modal
        ref="dialog"
        title-tag="h2"
        :title="title"
        lazy
        @shown="focus"
        @hidden="close"
        id="modal-vital-diagnostic-dialog"
    >
        <b-form-group
            label-cols="3"
            :label="isVital ? 'Vital:' : 'Diagnostic:'"
            label-for="type"
        >
            <b-select
                ref="type"
                v-model="typeId"
                :class="{ 'border-danger': errors.has('type') }"
                v-validate="'required'"
                name="type"
            >
                <option
                    v-for="item in typeList"
                    :key="'type-' + item.id"
                    :value="item.id"
                    >{{ item.vital }}</option
                >
            </b-select>
        </b-form-group>

        <b-form-group label-cols="3" label="Value:" label-for="value">
            <template v-for="item in typeList">
                <b-row v-show="typeId == item.id" :key="'val-' + item.id">
                    <b-col cols="7" class="pr-1" v-if="showValue">
                        <b-input
                            placeholder="Value (required)"
                            v-model="value"
                            type="number"
                            class="h-100"
                            :class="{ 'border-danger': errors.has('value') }"
                            v-validate="'required'"
                            name="value"
                        />
                    </b-col>
                    <b-col cols="5" class="pl-0" v-if="showUom">
                        <b-select
                            v-model="valueUnit"
                            :class="{
                                'border-danger': errors.has('value-unit')
                            }"
                            v-validate="'required'"
                            name="value-unit"
                        >
                            <option
                                v-for="uom in item.master_uom"
                                :key="'uom-' + uom.id"
                                :value="uom.id"
                                >{{ uom.uom }}</option
                            >
                        </b-select>
                    </b-col>
                    <b-col
                        v-if="showValue2"
                        :cols="
                            (showUom && showValue2) || (!showUom && !showValue)
                                ? '12'
                                : '5'
                        "
                        :class="{
                            'pl-0': !(
                                (showUom && showValue2) ||
                                (!showUom && !showValue)
                            ),
                            'mt-1': showMarginTopValue2
                        }"
                    >
                        <b-select
                            v-show="typeId == item.id"
                            v-model="value2"
                            :class="{ 'border-danger': errors.has('value2') }"
                            v-validate="'required'"
                            name="value2"
                        >
                            <option
                                v-for="(vitalVal, i) in item.master_vital_value"
                                :value="vitalVal.value"
                                :key="'vitalVal-' + vitalVal.id"
                                :selected="i === 0"
                                >{{ vitalVal.value }}</option
                            >
                        </b-select>
                    </b-col>
                </b-row>
            </template>
        </b-form-group>

        <b-form-group label-cols="3" label="Taken:" label-for="taken">
            <date-time-picker
                id="taken"
                v-model="taken"
                v-validate="'required'"
                :placeholder="{
                    date: 'Taken (required)',
                    time: 'Time (required)'
                }"
                :inner-class="{
                    'border-danger': errors.has('taken')
                }"
                rules="required"
                name="taken"
                container="#modal-vital-diagnostic-dialog"
                format="datetime"
            />
        </b-form-group>

        <b-form-group label-cols="3" label="Notes:" label-for="notes">
            <b-textarea placeholder="Notes (optional)" v-model="notes" />
        </b-form-group>

        <template v-slot:modal-footer="{ cancel, close }">
            <loader v-if="isSaving" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="cancel"
                v-if="!isSaving"
            >
                Cancel
            </b-button>

            <b-button-group v-if="!isSaving && !isEdit">
                <b-dropdown
                    @click="createNew(isShowButtonRepeat, close)"
                    left
                    split
                    :text="
                        isShowButtonRepeat ? 'Save + Add New' : 'Save + Done'
                    "
                    variant="primary"
                >
                    <b-dropdown-item
                        @click="createNew(!isShowButtonRepeat, close)"
                    >
                        {{
                            !isShowButtonRepeat
                                ? "Save + Add New"
                                : "Save + Done"
                        }}
                    </b-dropdown-item>
                </b-dropdown>
            </b-button-group>
            <b-button
                variant="primary"
                @click="saveChanges(close)"
                v-if="!isSaving && isEdit"
            >
                Save
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import { DateTime } from "luxon";
import { isEmpty } from "lodash";
import Loader from "@/module/commons/loader";
import CONSTANTS from "@/constants/history";
import History from "@/services/patient-history";
import DateTimePicker from "@/module/commons/date-time-picker.vue";
import EVENT from "@/constants/events";

export default {
    name: "ModalNewVital",
    components: {
        Loader,
        DateTimePicker
    },
    props: {
        isVital: {
            type: Boolean,
            default: false
        },
        patientId: {
            type: String
        },
        /**
         * Only used in new vital in medical note profile as soap cannot be changed in vital/diagnostic
         */
        soapId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            action: "add",
            diagnostic: "",
            typeList: [],
            typeId: "",
            value: "",
            value2: "",
            valueUnit: "",
            taken: null,
            notes: "",
            formError: [],
            timeFormat: "hh:mm A",
            isSaving: false,
            isShowButtonRepeat: false,
            record: null,
            loadCount: 0,
            showValue: true,
            showValue2: false,
            showMarginTopValue2: false,
            showUom: true,
            settings: null
        };
    },
    computed: {
        diagnosticList() {
            return this.$store.getters["references/diagnostics"];
        },
        vitalList() {
            return this.$store.state.reference["master-vital"];
        },
        isEdit() {
            return this.record ? true : false;
        },
        title() {
            let title = this.record ? "Edit " : "New ";

            return (title += this.isVital ? "Vital" : "Diagnostic");
        },
        /**
         * Differentiate between the patient profile and the lab profile
         */
        isSoap() {
            return this.$route.name === "soap";
        }
    },
    watch: {
        typeId: {
            handler: function handler(type) {
                if (type) {
                    // To skip resetValue if on open edit modal
                    if (!(this.loadCount < 1 && this.isEdit)) {
                        this.resetValue(type);
                    }
                }

                this.loadCount++;
            }
        },
        diagnostic(val) {
            if (val) {
                this.setShowValueFields(val);
            }
        },
        value(val) {
            if (val) {
                this.clearError("value");
            }
        }
    },
    methods: {
        setShowValueFields(type) {
            let uom_show = type.master_uom.length > 0;
            let values_show = this.isVital
                ? false
                : type.master_vital_value.length > 0;

            this.showMarginTopValue2 = false;

            if (type.multi_value == 2) {
                if (!uom_show) {
                    this.showUom = false;
                    this.showValue = true;
                    this.showValue2 = true;
                } else {
                    this.showValue = true;
                    this.showUom = true;
                    this.showValue2 = true;
                    this.showMarginTopValue2 = true;
                }
            } else {
                if (!uom_show && !values_show) {
                    this.showUom = false;
                    this.showValue = true;
                    this.showValue2 = false;
                } else {
                    if (uom_show) {
                        this.showUom = true;
                        this.showValue = true;
                        this.showValue2 = false;
                    } else if (values_show) {
                        this.showUom = false;
                        this.showValue = false;
                        this.showValue2 = true;
                        this.showMarginTopValue2 = true;
                    } else {
                        this.showUom = false;
                        this.showValue = false;
                        this.showValue2 = true;
                    }
                }
            }
        },
        async open(record, params) {
            this.settings = await this._getSetting();
            this.typeList = this.isVital ? this.vitalList : this.diagnosticList;

            if (record) {
                this.initRecord(_.clone(record));
            } else {
                this.initDateTime();
                if (this.typeList && this.typeList[0]) {
                    this.typeId = this.typeList[0].id;
                    this.resetValue(this.typeList[0].id);
                }
            }

            if (params) {
                this.typeId = params.typeId;
            }

            this.$refs.dialog.show();
        },
        focus() {
            this.$refs.type.focus();
        },
        initRecord(record) {
            this.loadCount = 0;
            this.record = record;

            this.typeId = record.master_vital_id;
            this.diagnostic = this.findType(record.master_vital_id)
                ? this.findType(record.master_vital_id)
                : record.master_vital;

            this.setShowValueFields(this.diagnostic);
            this.value = record.value;
            this.value2 = record.value2;
            this.valueUnit = record.master_uom_id;
            this.multiValue = record.master_vital
                ? record.master_vital.multi_value
                : 1;
            this.taken = this._fromDateCarbon(record.date);
            this.notes = record.note;
            if (this.multiValue == 1 && this.showValue2) {
                this.value2 = record.value;
                this.value = "";
            }
        },
        findType(typeId) {
            let details = _.find(this.typeList, function (item) {
                return item.id == typeId;
            });

            return details;
        },
        close() {
            this.$refs.dialog.hide();
            this.$emit("hidden");
        },
        initDateTime() {
            this.taken = DateTime.local().setZone(this._timeZone);
        },
        clearError(key) {
            this.formError = this.formError.filter(err => err !== key);
        },
        resetValue(type) {
            this.value = "";
            this.value2 = "";
            this.valueUnit = "";

            let details = this.findType(type);

            if (details) {
                this.diagnostic = details;
                this.multiValue = details.multi_value;

                if (
                    details.master_vital_value &&
                    details.master_vital_value.length > 0
                ) {
                    this.value2 = details.master_vital_value[0].value;
                }

                if (details.master_uom && details.master_uom.length > 0) {
                    let standard = this.settings && this.settings.weights_measures === 1; // prettier-ignore

                    // 4 = weight. default value = based on preferences
                    if (type === 4) {
                        if (standard) {
                            this.valueUnit = 75; // lbs
                        } else {
                            this.valueUnit = 72; // kg (metric)
                        }
                    } else if (type === 3) {
                        if (standard) {
                            this.valueUnit = 70; // F
                        } else {
                            this.valueUnit = 69; // C
                        }
                    } else {
                        this.valueUnit = details.master_uom[0].id;
                    }
                }
            }
        },
        resetFields() {
            this.value = "";
            this.notes = "";
            this.initDateTime();

            if (this.isVital) {
                this.typeId = this.vitalList[0].id;
                this.resetValue(this.vitalList[0].id);
            } else {
                this.typeId = this.diagnosticList[0].id;
                this.resetValue(this.diagnosticList[0].id);
            }

            this.$validator.reset();
        },
        async saveChanges(closeFn) {
            let valid = await this.$validator.validateAll();
            if (!valid) {
                return false;
            }

            this.isSaving = true;

            let params = {
                id: this.record.id,
                patient_id: this.patientId,
                master_uom_id: this.valueUnit,
                date: Number(this.taken.toFormat("X")),
                soap_id: this.record.soap_id,
                note: this.notes,
                master_vital_id: this.typeId
            };

            if (this.diagnostic.multi_value == 2) {
                params.value = this.value;
                params.value2 = this.value2;
            } else {
                params.value = !_.isEmpty(this.value)
                    ? this.value
                    : this.value2;
                params.value2 = "";
            }

            History.updateVital(this.patientId, params)
                .then(response => {
                    if (response.message.code === 200) {
                        this._message("Edits saved successfully.");

                        if (this.isVital) {
                            if (!this.isSoap) {
                                this.updatePatientProfile();
                            }
                            this.$emit("reload", CONSTANTS.VIEWS.VITALS);
                        } else {
                            this.$emit("reload", CONSTANTS.VIEWS.DIAGNOSTIC);
                        }

                        closeFn();
                    } else {
                        this._message(
                            "Error : Unable to update record",
                            "danger"
                        );
                    }

                    this.isSaving = false;
                })
                .catch(error => {
                    this._message("Error : Unable to update record", "danger");
                    this.isSaving = false;
                    console.error(error);
                });
        },
        async createNew(repeat = false, closeFn) {
            let valid = await this.$validator.validateAll();
            if (!valid) {
                return false;
            }

            this.isSaving = true;

            let params = {
                patient_id: this.patientId,
                master_uom_id: this.valueUnit,
                date: Number(this.taken.toFormat("X")),
                note: this.notes,
                master_vital_id: this.typeId
            };

            if (this.diagnostic.multi_value == 2) {
                params.value = this.value;
                params.value2 = this.value2;
            } else {
                params.value = !_.isEmpty(this.value)
                    ? this.value
                    : this.value2;
                params.value2 = "";
            }

            if (this.isSoap) {
                params.soap_id = this.soapId;
            }

            History.saveVital(this.patientId, params)
                .then(response => {
                    if (response.message.code === 200) {
                        this._message("Record saved successfully.");

                        if (this.isVital) {
                            if (!this.isSoap) {
                                this.updatePatientProfile();
                            }
                            this.$emit("reload", CONSTANTS.VIEWS.VITALS);
                        } else {
                            this.$emit("reload", CONSTANTS.VIEWS.DIAGNOSTIC);
                        }

                        if (!repeat) {
                            closeFn();
                        } else {
                            this.resetFields();
                            this.isShowButtonRepeat = repeat;
                            this.focus();
                        }

                        this.$tabEvent.emit(EVENT.REFRESH_PATIENT_PROFILE);
                    } else {
                        this._message(
                            "Error : Unable to added record",
                            "danger"
                        );
                    }
                    this.isSaving = false;
                })
                .catch(error => {
                    this.isSaving = false;
                    this._message("Error : Unable to added record", "danger");
                    console.error(error);
                });
        },
        updatePatientProfile() {
            var encodedId = this.$route.params.id;

            if (encodedId) {
                this.$store
                    .dispatch("patient/getPatientDetails", {
                        id: encodedId,
                        encoded: this.$route.query.encoded === "0" ? 0 : 1
                    })
                    .then(details => {
                        this.$root.$emit("update-patient-details", details);
                    });
            }
        }
    }
};
</script>
